// Bridges the live alert-fetch path to the on-device offline cache.
//
// Called with each freshly-fetched page of alerts. It persists metadata + the
// thumbnail bytes, but ONLY when the user enabled "sync with phone". Entirely best-effort and failure-tolerant:
// any error is swallowed so caching can never disturb the live view (same
// philosophy as the silent poll in AlertsPage/useAlerts).

import { apiFetch } from './streamApi';
import { isOfflineSyncEnabled } from './offlineSyncConfig';
import * as offline from './offlineAlertStore';

// Fetch + store one thumbnail if it isn't already cached. Best-effort.
async function cacheOneThumb(id) {
  if (await offline.hasThumb(id)) return;
  try {
    const r = await apiFetch(`/alerts/${encodeURIComponent(id)}/image`);
    if (r.ok) await offline.putThumb(id, await r.blob());
  } catch { /* skip this thumb */ }
}

export async function cacheAlertsToDevice(items) {
  if (!items?.length) return;
  if (!isOfflineSyncEnabled()) return;
  try {
    await offline.putAlerts(items);
    const queue = items.map(a => a.id || a.filename).filter(Boolean);
    const worker = async () => {
      for (;;) {
        const id = queue.shift();
        if (!id) return;
        await cacheOneThumb(id);
      }
    };
    await Promise.all([worker(), worker(), worker()]); // 3-way concurrency
    await offline.setLastSyncedAt(Date.now());
  } catch { /* best-effort */ }
}

// Enrich an already-cached alert with the evidence/verdict pulled by the detail
// view (/alerts/{id}/info). No-op unless sync is on.
// Also makes sure this alert has a thumbnail — putAlertDetail can create a
// list entry for an id that never went through cacheAlertsToDevice (e.g. the
// user opened an alert's info before it was ever paged through the list),
// and a metadata-only entry with no image would otherwise sit in the offline
// view forever showing just the placeholder icon.
export async function enrichOfflineDetail(filename, info) {
  if (!filename || !info) return;
  if (!isOfflineSyncEnabled()) return;
  const id = filename;
  const ev = info.ev ?? info.evidence ?? info;
  const verdict = info.verdict ?? null;
  try {
    await offline.putAlertDetail(id, ev, verdict);
    await cacheOneThumb(id);
  } catch { /* best-effort */ }
}
